import { useRef, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon,
  Undo2, Redo2, RemoveFormatting,
} from 'lucide-react';

// Basic allowlist (default — used by the existing Homepage content editor; unchanged behavior).
const BASIC_TAGS = ['p', 'br', 'b', 'strong', 'i', 'em', 'ul', 'ol', 'li'];
const BASIC_ATTR = [];
// Full allowlist for Regional News body + Resource description (matches the backend sanitizer).
const FULL_TAGS = ['p', 'br', 'h2', 'h3', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'a'];
const FULL_ATTR = ['href', 'title', 'target', 'rel'];

function makeSanitizer(full) {
  const ALLOWED_TAGS = full ? FULL_TAGS : BASIC_TAGS;
  const ALLOWED_ATTR = full ? FULL_ATTR : BASIC_ATTR;
  return (html) => DOMPurify.sanitize(html || '', { ALLOWED_TAGS, ALLOWED_ATTR });
}

export function RichTextEditor({ value, onChange, placeholder, full = false, testId }) {
  const editorRef = useRef(null);
  const lastEmittedRef = useRef(value || '');
  const sanitize = useRef(makeSanitizer(full)).current;

  // Make Enter create real <p> blocks (Chrome defaults to <div>, which the sanitizer strips).
  useEffect(() => {
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* noop */ }
  }, []);

  // Only set innerHTML when value changes externally (not from our own typing)
  useEffect(() => {
    if (!editorRef.current) return;
    const sanitized = sanitize(value || '');
    if (sanitized !== lastEmittedRef.current) {
      editorRef.current.innerHTML = sanitized;
      lastEmittedRef.current = sanitized;
    }
  }, [value, sanitize]);

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    const clean = sanitize(editorRef.current.innerHTML);
    lastEmittedRef.current = clean;
    onChange(clean);
  }, [onChange, sanitize]);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  }, [emit]);

  // Paragraph / H2 / H3 block formatting.
  const block = useCallback((tag) => exec('formatBlock', `<${tag}>`), [exec]);

  // Insert/edit a link on the current selection. Only http(s)/mailto are accepted;
  // anything else (javascript:, data:, etc.) is rejected before it can be applied.
  const insertLink = useCallback(() => {
    editorRef.current?.focus();
    const url = (window.prompt('Link URL (https://…)') || '').trim();
    if (!url) return;
    if (!/^(https?:\/\/|mailto:)/i.test(url)) {
      window.alert('Please enter a valid http(s) or mailto link.');
      return;
    }
    document.execCommand('createLink', false, url);
    emit();
  }, [emit]);

  // Clear formatting: strip inline styling and unlink the selection.
  const clearFormatting = useCallback(() => {
    editorRef.current?.focus();
    document.execCommand('removeFormat');
    document.execCommand('unlink');
    emit();
  }, [emit]);

  const handleInput = useCallback(() => emit(), [emit]);

  // Paste as PLAIN TEXT normalized into semantic paragraph blocks. Browsers otherwise wrap pasted
  // lines in <div>, which the sanitizer strips (div is not allowlisted) — collapsing paragraph
  // spacing on save/reopen. We split on blank lines into <p>…</p> (single newlines → <br>), so the
  // paragraph structure survives sanitization and renders with proper .rte-content spacing.
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const raw = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const paras = raw.replace(/\r\n?/g, '\n').split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
    if (paras.length === 0) return;
    const html = paras.map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
    document.execCommand('insertHTML', false, html);
    emit();
  }, [emit]);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-slate-200 bg-slate-50">
        {full && (
          <>
            <ToolBtn onClick={() => block('p')} title="Paragraph" testid="rte-paragraph"><span className="text-xs font-semibold">P</span></ToolBtn>
            <ToolBtn onClick={() => block('h2')} title="Heading 2" testid="rte-h2"><span className="text-xs font-bold">H2</span></ToolBtn>
            <ToolBtn onClick={() => block('h3')} title="Heading 3" testid="rte-h3"><span className="text-xs font-bold">H3</span></ToolBtn>
            <Divider />
          </>
        )}
        <ToolBtn onClick={() => exec('bold')} title="Bold (Ctrl/Cmd+B)" testid="rte-bold"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Italic (Ctrl/Cmd+I)" testid="rte-italic"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        {full && (
          <ToolBtn onClick={() => exec('underline')} title="Underline (Ctrl/Cmd+U)" testid="rte-underline"><UnderlineIcon className="w-3.5 h-3.5" /></ToolBtn>
        )}
        <Divider />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bulleted list" testid="rte-ul"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Numbered list" testid="rte-ol"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
        {full && (
          <>
            <Divider />
            <ToolBtn onClick={insertLink} title="Insert/Edit link" testid="rte-link"><LinkIcon className="w-3.5 h-3.5" /></ToolBtn>
            <Divider />
            <ToolBtn onClick={() => exec('undo')} title="Undo" testid="rte-undo"><Undo2 className="w-3.5 h-3.5" /></ToolBtn>
            <ToolBtn onClick={() => exec('redo')} title="Redo" testid="rte-redo"><Redo2 className="w-3.5 h-3.5" /></ToolBtn>
            <ToolBtn onClick={clearFormatting} title="Clear formatting" testid="rte-clear"><RemoveFormatting className="w-3.5 h-3.5" /></ToolBtn>
          </>
        )}
      </div>

      {/* Editor — NO dangerouslySetInnerHTML to avoid cursor reset */}
      <div
        ref={(el) => {
          editorRef.current = el;
          if (el && !el.dataset.initialized) {
            el.innerHTML = sanitize(value || '');
            el.dataset.initialized = 'true';
          }
        }}
        contentEditable
        suppressContentEditableWarning
        className={`rte-editor ${full ? 'rte-content ' : ''}min-h-[140px] max-h-[420px] overflow-y-auto px-3 py-2 text-sm leading-relaxed focus:outline-none`}
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        data-testid={testId ? `${testId}-area` : undefined}
      />
    </div>
  );
}

function Divider() {
  return <span className="w-px h-5 bg-slate-200 mx-0.5" />;
}

function ToolBtn({ onClick, title, children, testid }) {
  return (
    <button
      type="button"
      // Prevent the button from stealing the selection before the command runs.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="inline-flex items-center justify-center min-w-[26px] h-7 px-1.5 rounded text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors"
    >
      {children}
    </button>
  );
}

export default RichTextEditor;
